/**
 * Shared auth display helpers
 *
 * SSR-safe utilities for deriving user-facing labels from auth payloads.
 * Accepts both camelCase and snake_case field names (BFF / Zitadel / legacy).
 * Does NOT read localStorage or other browser-only storage.
 */

/** Minimal user shape accepted by display helpers (loose for multi-source auth). */
export type DisplayUser = {
  id?: string;
  email?: string | null;
  username?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  roles?: string[] | null;
  role?: string | null;
  userType?: string | null;
  user_type?: string | null;
  audience?: string | null;
  clientData?: unknown;
  client_data?: unknown;
} | null | undefined;

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  superadmin: 'Super Admin',
  super_admin: 'Super Admin',
  manager: 'Manager',
  trader: 'Trader',
  client: 'Client',
  viewer: 'Viewer',
  auditor: 'Auditor',
  employee: 'Employee',
  operator: 'Operator',
  analyst: 'Analyst',
  compliance: 'Compliance',
};

function pickFirstName(user: NonNullable<DisplayUser>): string {
  return (
    user.firstName ||
    user.first_name ||
    user.given_name ||
    ''
  ).trim();
}

function pickLastName(user: NonNullable<DisplayUser>): string {
  return (
    user.lastName ||
    user.last_name ||
    user.family_name ||
    ''
  ).trim();
}

/**
 * Human-readable display name for avatars, menus, and greetings.
 * Prefers first + last, then name, username, then email local-part
 * (e.g. dev.client@… → "Dev"), then "User".
 */
export function getUserDisplayName(user: DisplayUser): string {
  if (!user) return 'User';

  const first = pickFirstName(user);
  const last = pickLastName(user);
  const full = [first, last].filter(Boolean).join(' ').trim();
  if (full) return full;

  const name = (user.name || '').trim();
  if (name) return name;

  const username = (user.username || '').trim();
  if (username) return username;

  const email = (user.email || '').trim();
  if (email) {
    const local = email.split('@')[0]?.trim() || '';
    if (local) {
      // First segment of dotted/underscored locals: dev.client → Dev
      const segment = local.split(/[._-]/)[0] || local;
      return segment.charAt(0).toUpperCase() + segment.slice(1);
    }
  }

  return 'User';
}

/** First-name style greeting token (Welcome back, X). */
export function getUserGreetingName(user: DisplayUser): string {
  if (!user) return 'there';
  const first = pickFirstName(user);
  if (first) return first;
  return getUserDisplayName(user) === 'User' ? 'there' : getUserDisplayName(user);
}

/**
 * 1–2 character initials for Avatar fallbacks.
 * Uses first/last name when available, otherwise email/username initial.
 */
export function getUserInitials(user: DisplayUser): string {
  if (!user) return 'U';

  const first = pickFirstName(user);
  const last = pickLastName(user);

  if (first || last) {
    const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    if (initials) return initials;
  }

  const name = (user.name || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]!.charAt(0)}${parts[1]!.charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  const email = (user.email || '').trim();
  if (email) return email.charAt(0).toUpperCase();

  const username = (user.username || '').trim();
  if (username) return username.charAt(0).toUpperCase();

  return 'U';
}

/**
 * Friendly role label for account menus / badges.
 * Prefers primary role, then userType / audience.
 */
export function getUserRoleLabel(user: DisplayUser): string {
  if (!user) return 'User';

  const rawRole =
    (user.roles && user.roles.length > 0 ? user.roles[0] : null) ||
    user.role ||
    user.userType ||
    user.user_type ||
    user.audience ||
    null;

  if (!rawRole) return 'User';

  const key = String(rawRole).trim().toLowerCase();
  if (ROLE_LABELS[key]) return ROLE_LABELS[key]!;

  // Title-case unknown roles: "portfolio_manager" → "Portfolio Manager"
  return String(rawRole)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Whether the user is a client-portal user.
 *
 * SSR-safe: inspects roles, userType, audience, and clientData only.
 * Does not read localStorage or cookies.
 */
export function isClientUser(user: DisplayUser): boolean {
  if (!user) return false;

  const userType = (user.userType || user.user_type || '').toString().toLowerCase();
  if (userType === 'client') return true;

  const audience = (user.audience || '').toString().toLowerCase();
  if (audience === 'client') return true;

  if (user.clientData != null || user.client_data != null) return true;

  if (Array.isArray(user.roles)) {
    const roles = user.roles.map((r) => String(r).toLowerCase());
    if (roles.includes('client')) return true;
  }

  const singleRole = (user.role || '').toString().toLowerCase();
  if (singleRole === 'client') return true;

  return false;
}
